import _ from 'underscore';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import Constants from 'core/constants';
import BaseController from 'controllers/base';
import DashboardDataModel from 'models/clients/carddata';
import { hasFeature } from 'core/utils/feature-flags';
import JSTimezoneDetect from 'jstimezonedetect';
import { canFetchCardData } from 'core/utils/card';
import addDimensionForKPI from 'core/utils/add-dimension-for-kpi';
import featureFlags from 'core/featureFlags';
import mixinCardDate from 'core/utils/mixin-card-date';
import { isCardDataEmpty } from 'views/panels/dashboards/utils/carddata';

const { ODI, DASHBOARD } = Constants;

class DashboardsDataController extends BaseController {
  constructor() {
    super(arguments, {
      actions: {
        'dashboards-data:dashboard:get': '_getDashboardData',
        'dashboards-data:card:get': '_getCardData',
        'dashboards-data:card:reset': '_resetCardData',
        'dashboards-data:card:reset:wordcloud': '_resetWordcloudData',
        'dashboards-data:card:rest:get': '_getRestData',
        'dashboards-data:card:rest:data:get': '_getCardRestData',
        'dashboards-data:card:rest:set': '_setRestData',
        'dashboards-data:card:rest:poll': '_pollCardRestData',
        'dashboards-data:odi:cancel': '_cancelOdi',
        'dashboards-data:odi:get': '_getOdi',
        'dashboards-data:card:rest:unset': '_unsetRestData',
        'dashboards-data:global-date-filter:set': '_setGlobalDateFilter',
        'dashboards-data:global-date-filters:set': '_setGlobalDateFilters',
        'dashboards-data:pagination:set': '_setPagination',
        'dashboards-data:pagination:unset': '_unsetPagination',
        'dashboards-data:destroy-single-auto-cycle-timers':
          '_destroySingleAutoCycleTimers',
        'dashboards-data:autocycle-pagination:set': '_setAutoCyclePagination',
        'dashboards-data:clear-auto-cycle-pagination':
          '_clearAutoCyclePagination',
      },
      name: 'dashboards-data',
      dependsOn: ['user', 'authorization', 'dates'],
    });

    this.offsetLoadGuard = null;
    this.currentCards = [];
    this.pendingUpdates = {};
    this.pendingCardRequests = {};
    this.pagination = {};
    this.timeZoneParam = JSTimezoneDetect.determine().name();
    this.autoCycleTimers = [];
    this.autoCyclePagination = {};

    try {
      const persistedGDF = window.localStorage.getItem('persistedGDF') || false;
      this.globalDateFilter = JSON.parse(persistedGDF) || {};
    } catch (err) {
      this.globalDateFilter = {};
    }
    window.localStorage.removeItem('persistedGDF');
  }

  initialize(opts) {
    const options = opts || {};
    options.clientId = this.getClientId();
  }

  _getAttribute(card, key, fallback) {
    if (card && card.metadata && card.metadata.attributes) {
      return card.metadata.attributes[key];
    }
    if (
      card.metadata.attributes[key] === null ||
      card.metadata.attributes[key] === undefined
    ) {
      return fallback;
    }
    return fallback;
  }

  _getCurrentCardData(cardId, callback) {
    this.publish('app:getData', {
      key: 'cardDetailData',
      callback: (cardDetailData) => {
        cardDetailData =
          cardDetailData && cardDetailData.cardId === cardId
            ? cardDetailData
            : {};
        callback(cardDetailData);
      },
    });
  }

  _resetCardData(payload) {
    this._updateCardData(
      {
        ...payload,
        isOnDetail: true,
      },
      false
    );
  }

  _getDateDisplay(card, data) {
    const chartType = this._getAttribute(card, 'chartType');
    const dateRange = this._getAttribute(card, 'dateRange');
    const selectedDateId = this._getAttribute(card, 'selectedDateId');
    const datePickerButtons = hasFeature('additional-date-ranges')
      ? Constants.datePickerAdditionalButtons
      : Constants.datePickerButtons;
    const selectedButton = _.findWhere(datePickerButtons, {
      id: selectedDateId,
    });

    if (chartType === Constants.DASHBOARD.chartTypes.priorityIndex) {
      return this.getString('general.multipleDates');
    } else if (dateRange && dateRange.f && dateRange.l) {
      return this._parseRange(dateRange);
    } else if (selectedButton && selectedDateId !== 'custom') {
      if (dateRange.t === 'DATE_TIME' && selectedDateId === 'yesterday') {
        return this.getString('datePicker.presetMenu.last24Hours');
      }
      return this.getString(selectedButton.label);
    } else if (
      data &&
      data.series &&
      data.series.length &&
      data.series[0].parsedDateRange
    ) {
      return data.series[0].parsedDateRange;
    } else if (chartType === Constants.DASHBOARD.chartTypes.gauge) {
      return data.primaryScore ? data.primaryScore.parsedDateRange : '';
    } else if (
      chartType === Constants.DASHBOARD.chartTypes.voc &&
      data.dateRange
    ) {
      return this._parseRange(data.dateRange);
    }
    return '';
  }

  //Only update appData once all card data is loaded to prevent too much thrashing
  _updateCardData(card, data, callback) {
    if (card.isOnDetail) {
      this.publish('app:updateData', {
        cardDetailData: {
          ...data,
          cardId: card.cardId,
        },
      });
      return;
    }
    const d = data
      ? {
          ...data,
          dateRangeDisplay: this._getDateDisplay(card, data),
        }
      : d;
    const { cardId } = card;
    this.pendingUpdates[cardId] = d;
    if (this.currentCards) {
      const hasAllData = !_.find(this.currentCards, (c) => {
        return !this.pendingUpdates[c.cardId];
      });
      if (hasAllData) {
        this.publish('app:getData', {
          key: 'cardData',
          callback: (cardData) => {
            cardData = cardData || {};
            this.publish('app:updateData', {
              cardData: {
                ...cardData,
                ...this.pendingUpdates,
              },
            });
            this.currentCards = [];
            this.pendingUpdates = {};
          },
        });
      }
    }
  }

  _parseRange(range) {
    const start = new moment(range.f).format(Constants.dateDisplay);
    const end = new moment(range.l).format(Constants.dateDisplay);
    return `${start} to ${end}`;
  }

  _getDashboardData({ cards, isReload = false }) {
    this.currentCards = [];
    if (isReload) {
      this._cancelPollCardRestData();
    }

    this.publish('app:getData', {
      key: 'cardRestData',
      callback: (cardRestData = {}) => {
        this.getCurrentDashboardType((dashboardType) => {
          const isCustomDashboards = dashboardType === 'CUSTOM';

          // reset rest cards as necessary
          cards.forEach((card) => {
            if (!isCustomDashboards || isReload) {
              cardRestData[card?.cardId] = false;
            }
          });

          this.publish('app:updateData', {
            cardRestData,
            callback: () => {
              this._getDashboardDataStarter({
                cards,
                cardRestData,
                isCustomDashboards,
                isReload,
              });
            },
          });
        });
      },
    });
  }

  _getDashboardDataStarter({
    cards,
    cardRestData,
    isCustomDashboards,
    isReload,
  }) {
    // set the 'offsetLoadGuard' - this is so that if a different set of
    // cards is requested it will cause any previous loop that is still
    // running to exit.
    const guard = new Date();
    this.offsetLoadGuard = guard;
    const opts = {
      cardRestData,
      cards,
      isCustomDashboards,
      isReload,
      guard,
      timeZone: this.timeZoneParam,
    };
    if (isCustomDashboards && !isReload && cardRestData) {
      cards.forEach((card) => {
        const action = this._getRestDataAction(card.isOnDetail);
        const currentCardData = cardRestData[card.cardId];
        if (currentCardData) {
          if (this.shouldResetCache(currentCardData)) {
            cardRestData[card.cardId] = false;
          } else {
            this.publish(action, currentCardData);
          }
        }
      });
    }
    this._offsetCardLoading(opts, 0);
  }

  // With HTTP/2, if a customer has a large dashboard, then all the cards would
  // be requested simultaneously, putting a lot pressure on downstream services.
  // This method allows cards to be loaded sequentially, after an offset period.
  // For custom dashboards, if the data for a given rest card is already
  // retrieved it is re-dispatched and the next card requested immediately.
  _offsetCardLoading(opts, cardIndex) {
    const {
      cards,
      cardRestData,
      isReload,
      isCustomDashboards,
      guard,
      timeZone,
    } = opts;
    const card = cards && cards[cardIndex];
    if (!card || guard !== this.offsetLoadGuard) {
      return;
    }
    let timeout = Constants.DASHBOARD.CARD_LOAD_OFFSET_MILLIS;
    const pollingReference = this._getPollingReference(card?.cardId);
    if (isReload && this._isPollingCard(card)) {
      this._resetRestCard(card);
    } else if (
      !cardRestData ||
      !cardRestData[card.cardId] ||
      !isCustomDashboards ||
      isReload
    ) {
      this._getRestData({ card, timeZone });
    } else if (pollingReference) {
      this._resumePolling(pollingReference, card?.isOnDetail);
    } else {
      timeout = 0;
    }
    setTimeout(() => this._offsetCardLoading(opts, cardIndex + 1), timeout);
  }

  getCurrentDashboardType(callback) {
    this.publish('route:get', {
      callback: (data) => {
        let dashboardType;
        if (data.route.startsWith('dashboards')) {
          dashboardType = 'CUSTOM';
        }
        callback(dashboardType);
      },
    });
  }

  _getCardData(card) {
    const newCard = cloneDeep({ isOnDetail: true, ...card });
    const chartType = this._getAttribute(card, 'chartType');

    if (newCard.isOnDetail) {
      if (newCard.autoFetch) {
        this._resetRestCard(newCard, { status: null });
        this._getRestData({ card: newCard });
      } else if (chartType === 'scoreSummary' || chartType === 'priorityMap') {
        if (!newCard.isInitialization) {
          this._resetRestCard(newCard);
        }
        this._updateCardData(newCard);
      } else {
        this._resetRestCard(card, { status: null });
        this._getRestData({ card: newCard });
      }
    } else {
      this.publish('app:getData', {
        key: 'cardData',
        callback: (cardData) => {
          cardData = cardData || {};
          cardData[newCard.cardId] = false;
          this.publish('app:updateData', {
            cardData,
            callback: () => {
              this._getCardRestData({ card: newCard });
            },
          });
        },
      });
    }
  }

  _getRestData(payload) {
    const { card, cardData } = payload;
    const chartType = this._getAttribute(card, 'chartType');
    const pollingReference = this._getPollingReference(card?.cardId);
    if (pollingReference) {
      this._resumePolling(pollingReference, card?.isOnDetail);
    } else if (!canFetchCardData(card)) {
      this._resetRestCard(card);
    } else if (this._isPollingCard(card)) {
      this._getOdi(payload);
    } else {
      const action = this._getRestDataAction(card.isOnDetail);
      if (chartType === 'voc' && card.getNextPage) {
        this.publish(action, { ...cardData, loading: true });
      } else {
        this.publish(action, { cardId: card.cardId });
      }
      if (chartType === 'voc') {
        this._getVoc(payload);
      } else {
        this._getCardRestData(payload);
      }
    }
  }

  _isPollingCard(card) {
    const chartType = this._getAttribute(card, 'chartType');
    return chartType === 'scoreSummary' || chartType === 'priorityMap';
  }

  _resetRestCard(card, data = {}) {
    const action = this._getRestDataAction(card.isOnDetail);
    if (!this._isPollingCard(card)) {
      this.publish(action, { cardId: card.cardId, ...data });
    } else {
      this.publish(action, {
        cardId: card.cardId,
        status: 'GENERATE',
        ...data,
      });
      if (this.pollCardRestDataTimer) {
        this._cancelPollCardRestData(card.cardId);
      }
    }
    if (this.pendingCardRequests[card.cardId]) {
      this._cancelPendingCardRequests(card.cardId);
    }
  }

  _cancelPendingCardRequests(cardId) {
    (this.pendingCardRequests[cardId] || []).forEach((cardRequest) => {
      cardRequest._ajaxRef.abort();
    });
  }

  _getRestDataAction(isOnDetail) {
    if (isOnDetail) {
      return 'dashboards-data:card:rest:data';
    }
    return 'dashboards-data:cards:rest:data';
  }

  _getOdi(data) {
    const { options = {}, card, timeZone } = data;
    const dataSources = this._getAttribute(card, 'dataSources') || [];
    const allDataSources = this._getAttribute(card, 'allDataSources') || false;

    if (dataSources?.length === 0 && !allDataSources) {
      return;
    }
    this._getCardRestData({
      card: card,
      timeZone,
      overrideMaxRC: options.overrideMaxRC,
      callback: (cardData) => {
        const isOnDetail = card.isOnDetail;
        const action = this._getRestDataAction(isOnDetail);
        const { status, hasErrors } = cardData;
        if (cardData.ready) {
          this.publish(action, { ...cardData, status: ODI.COMPLETED });
        } else if (
          status !== ODI.TOO_FEW_RESPONDENTS &&
          status !== ODI.TOO_MANY_RESPONDENTS &&
          status !== ODI.PARTITION_THRESHOLD_NOT_MET &&
          status !== ODI.RC_LIMIT_EXCEEDED &&
          status !== ODI.ERROR &&
          status !== ODI.ENGINE_ERROR &&
          status !== ODI.NOT_ENOUGH_DATA_POINTS &&
          !hasErrors
        ) {
          this.publish(action, cardData);
          this._pollCardRestData({
            card: cardData,
            isOnDetail,
          });
        } else {
          this.publish(action, cardData);
        }
      },
    });
  }

  _destroyAutoCycleTimers() {
    this.autoCycleTimers.forEach((obj) =>
      Object.values(obj).forEach(clearTimeout)
    );
    this.autoCycleTimers.length = 0;
  }

  _destroySingleAutoCycleTimers({ cardId }) {
    this.autoCycleTimers?.forEach((t) => {
      if (t[cardId]) {
        clearTimeout(t[cardId]);
        delete t[cardId];
      }
    });
  }

  _getVoc(payload) {
    const { card, cardData = {}, timeZone } = payload;
    const { isOnDetail, metadata, cardId } = card;

    if (isOnDetail && this.autoCycleTimers?.length) {
      //Destroy timers if not on dashboard view so it doesnt keep requesting data
      this._destroyAutoCycleTimers();
    }

    const { viewType, cardSize, cardHeight, autoCycle } = metadata.attributes;
    const isMosaicView = viewType === 'card';
    const cardPagination = this.pagination[cardId];
    const shouldStartAutoCycle =
      autoCycle &&
      isMosaicView &&
      cardSize === 'full' &&
      cardHeight === 'fullScreen' &&
      !isOnDetail;
    const DEFAULT_VOC_COMMENT_COUNT = 25;
    // suppress card requests if we don't know the card size yet.
    if (isMosaicView && !cardPagination?.limit) {
      return;
    }

    let offset = cardData.offset || 0;
    let limit = null;
    const hasNoExportValues = !cardData.exportValues?.length;
    const commentCount =
      card?.metadata?.attributes?.commentCount || DEFAULT_VOC_COMMENT_COUNT;
    if (
      isMosaicView &&
      cardPagination?.limit < commentCount &&
      !cardPagination.offset?.current &&
      hasNoExportValues
    ) {
      limit = commentCount;
      offset = 0;
    } else if (isMosaicView) {
      // NB 'offset' in our UI is a specific item index; but 'offset' in
      // respondents-service is the page number.
      limit = cardPagination.limit;
      offset = isOnDetail
        ? Math.floor((cardPagination.offset?.current || 0) / limit)
        : 0;
    }

    if (shouldStartAutoCycle) {
      if (this.autoCyclePagination[cardId]) {
        offset = this.autoCyclePagination[cardId].offset;
        limit = this.autoCyclePagination[cardId].limit;
      }
      this.autoCyclePagination[cardId] = { limit, offset };
    }
    // end autocycle pagination

    this._getCardRestData({
      card,
      timeZone,
      offset,
      limit,
      callback: (response) => {
        const action = this._getRestDataAction(isOnDetail);
        if (isMosaicView && isOnDetail) {
          if (
            isMosaicView &&
            cardPagination.drillInOffset >= 0 &&
            !cardData.exportValues?.length
          ) {
            cardPagination.offset.current = cardPagination.drillInOffset;
            //Request respondents at drillInOffset value
            //And set exportValues in the new response
            this._getCardRestData({
              card,
              timeZone,
              offset: Math.floor(
                cardPagination.drillInOffset / cardPagination.limit
              ),
              limit: cardPagination.limit,
              callback: (newResponse) => {
                (newResponse.exportValues = [...response.respondents]),
                  this.publish(action, newResponse);
              },
            });
            return;
          }

          const respOffset = limit * offset;
          if (cardPagination.offset?.current !== respOffset) {
            // update offset if it changed via resizing
            cardPagination.offset.current = respOffset;
          }
          const totalCount = response.totalCount || 0;
          const totalPages = Math.floor(totalCount / limit);
          if (offset > totalPages) {
            // if a change in configuration has meant our last page no longer
            // exists, then reset to the first page.
            cardPagination.offset.current = 0;
            this._getCardRestData({
              card,
              timeZone,
              limit,
              callback: (newResponse) => {
                this.publish(action, newResponse);
              },
            });
            return;
          }
        }

        const isPaginatedResponse =
          cardData.offset && cardData.offset !== response.offset;
        let updatedCardData = response;
        if (isMosaicView && offset === 0 && !cardData.exportValues?.length) {
          response.exportValues = response.respondents;
          //Set respondents to limit so pagination can work
          response.respondents = response?.respondents?.slice(
            0,
            cardPagination.limit
          );
        } else if (isMosaicView && cardData.exportValues?.length) {
          updatedCardData = {
            ...response,
            exportValues: [...cardData.exportValues],
          };
          if (!isOnDetail) {
            //Update cardData so we have exportValues available
            //If dashboard export is clicked
            this.publish('app:updateData', {
              cardData: { [card.cardId]: { ...updatedCardData } } || {},
            });
          }
        }
        if (isPaginatedResponse && !isMosaicView) {
          updatedCardData = {
            ...response,
            respondents: [...cardData.respondents, ...response.respondents],
          };
        }

        updatedCardData.autoCycleLimit = limit;
        updatedCardData.autoCycleOffset = offset;
        this.publish(action, updatedCardData);

        if (shouldStartAutoCycle) {
          this._destroySingleAutoCycleTimers(card);
          this._scheduleNextAutoCycleRequest({
            offset: offset,
            payload: updatedCardData,
            limit: this.pagination[cardId]?.limit,
            card,
            timeZone,
            cardData,
          });
        }
      },
    });
  }

  _scheduleNextAutoCycleRequest = ({
    offset,
    payload,
    limit,
    card,
    timeZone,
    cardData,
  }) => {
    this.autoCycleTimers.push({
      [card.cardId]: setTimeout(() => {
        let scheduleOffset = offset;
        let cycleRestart = false;
        if (document.visibilityState === 'visible') {
          scheduleOffset = offset + 1;
          if (scheduleOffset * limit >= payload.totalCount) {
            //Reset to 0 to go back to page 1
            scheduleOffset = 0;
            cycleRestart = true;
          }

          this._getCardRestData({
            card,
            timeZone,
            limit: limit,
            offset: scheduleOffset,
            callback: (newResponse) => {
              if (!newResponse.hasErrors) {
                newResponse.exportValues = [...newResponse?.respondents];
              }

              newResponse.autoCycleOffset = scheduleOffset;
              newResponse.autoCycleLimit = limit;
              newResponse.cycleRestart = cycleRestart;

              this.autoCyclePagination[card.cardId] = {
                limit,
                offset: scheduleOffset,
              };
              this.publish('dashboards-data:cards:rest:data', newResponse);
              this._scheduleNextAutoCycleRequest({
                offset: scheduleOffset,
                payload: newResponse,
                limit,
                card,
                timeZone,
                cardData,
              });
            },
          });
        } else {
          this._scheduleNextAutoCycleRequest({
            offset: scheduleOffset,
            payload,
            limit,
            card,
            timeZone,
            cardData,
          });
        }
      }, DASHBOARD.AUTO_CYCLE_TIMEOUT_MILLIS),
    });
  };

  _setGlobalDateFilter({ dashboardId, globalDateFilter, callback = () => {} }) {
    this.globalDateFilter[dashboardId] = globalDateFilter;
    this.autoCyclePagination = {};
    callback();
  }

  _setGlobalDateFilters({ globalDateFilters }) {
    this.globalDateFilter = globalDateFilters;
  }

  _setAutoCyclePagination({ cardId, offset = 0, limit }) {
    const { offset: currentOffset, limit: currentLimit } =
      this.autoCyclePagination[cardId] || {};
    let newOffset = currentOffset;
    let newLimit = currentLimit;

    if (currentLimit !== limit) {
      newLimit = limit;
      newOffset = offset;
    }

    this.autoCyclePagination[cardId] = {
      offset: newOffset,
      limit: newLimit,
    };
  }

  _setPagination({ cardId, ...paginationObj }) {
    this.pagination[cardId] = paginationObj;
  }

  _unsetPagination({ cardId }) {
    delete this.pagination[cardId];
  }

  _addGlobalDateFilter(card) {
    const { dashboardId } = card;
    const currentGdf = this.globalDateFilter[dashboardId];
    if (
      !currentGdf?.isEnabled ||
      currentGdf?.dateRange?.selectedDateId === undefined
    ) {
      return card;
    }
    return mixinCardDate(
      card,
      currentGdf.dateRange,
      currentGdf.dateRange.selectedDateId
    );
  }

  _getCardRestData(payload) {
    const card = payload.card || payload || {};
    const { timeZone } = payload;
    this.addTimezone(card, timeZone);
    let cardUpdated = {
      ...card,
      metadata: {
        ...card.metadata,
        attributes: { ...card.metadata.attributes },
      },
    };

    cardUpdated = this._addGlobalDateFilter(cardUpdated);
    cardUpdated = addDimensionForKPI(cardUpdated);

    const chartType = this._getAttribute(card, 'chartType');
    const { attributes } = cardUpdated.metadata;

    if (!attributes.hierarchyFilterId) {
      delete cardUpdated.metadata.attributes.hierarchyFilterId;
    }
    if (!attributes.modelFilterId) {
      delete cardUpdated.metadata.attributes.modelFilterId;
    }
    if (!attributes.pageFilterId) {
      delete cardUpdated.metadata.attributes.pageFilterId;
    }
    if (
      !featureFlags.hasFeature('enable-benchmarks') &&
      (attributes?.benchmarkKeys || attributes?.benchmarkType)
    ) {
      delete cardUpdated.metadata.attributes.benchmarkKeys;
      delete cardUpdated.metadata.attributes.benchmarkType;
    }
    if (
      chartType === Constants.DASHBOARD.chartTypes.scoreSummary &&
      attributes?.['viewType'] !== 'bar'
    ) {
      cardUpdated.metadata.attributes.sortByImpact = false;
    }
    ['dimensions', 'dimensionRows', 'dimensionColumns'].forEach((key) => {
      if (attributes[key]?.length > 0) {
        cardUpdated.metadata.attributes[key] = attributes[key].map(
          (dimension) => {
            const { propertyItems: _omitted, ...rest } = dimension;
            return rest;
          }
        );
      }
    });
    const useCommentDataService = chartType === 'voc';
    const useImpactDataService = this._isImpactChartType(chartType);
    const useMetricDataService =
      chartType !== 'voc' && !this._isImpactChartType(chartType);

    if (useCommentDataService) {
      cardUpdated.metadata.attributes.includeRespondentLabels = true;
    }

    const cardModel = new DashboardDataModel(cardUpdated, {
      clientId: this.getClientId(),
      useCommentDataService,
      useImpactDataService,
      useMetricDataService,
      urlparams: {
        overrideMaxRC: payload.overrideMaxRC,
        offset: payload.offset,
        limit: payload.limit,
      },
    });

    cardModel
      .save(cardUpdated, {
        doNotAbort: card.isOnDetail ? true : false,
        error: (m, r) => {
          this._onCardRestDataError(
            payload,
            r.status,
            r.responseJSON?.message,
            r.responseJSON?.error
          );
        },
      })
      .then((response) => {
        const { hasErrors, cardErrorCode } = response;

        // FYI backbone 'save' represents updating a model - so cardModel
        // at this point contains the mix of both the request and the response
        // i.e. both the score data and the request attributes. This should be
        // clearned up in the future so that we only keep the response part.
        let cardResponse = cardModel.toJSON();

        let cardError = false;
        if (hasErrors && cardErrorCode) {
          cardError = this.getString(
            `dashboards.${this._getAttribute(
              card,
              'chartType'
            )}.errorCode.${cardErrorCode}`
          );
        }
        cardResponse = {
          ...cardResponse,
          cardError,
          gdfEnabled: this.globalDateFilter[card.dashboardId]?.isEnabled,
        };
        if (this.cardHasHourlyData(cardResponse)) {
          const now = moment();
          if (this.isCardToDate(cardResponse, now)) {
            const newDate = now.add(1, 'hour');
            //properties set to 0 to round newDate to hour
            cardResponse.cacheExpirationTime = newDate.set({
              minute: 0,
              second: 0,
              millisecond: 0,
            });
          }
        }
        if (payload.callback) {
          payload.callback(cardResponse);
        } else {
          const action = this._getRestDataAction(cardResponse.isOnDetail);
          this.publish(action, cardResponse);
        }
        if (
          (!isCardDataEmpty(response) ||
            cardResponse.status === ODI.TOO_FEW_RESPONDENTS ||
            cardResponse.status === ODI.NOT_ENOUGH_DATA_POINTS ||
            cardResponse.status === ODI.TOO_MANY_RESPONDENTS ||
            cardResponse.status === ODI.PARTITION_THRESHOLD_NOT_MET ||
            cardResponse.status === ODI.RC_LIMIT_EXCEEDED) &&
          !cardResponse.isOnDetail
        ) {
          this._setRestData({ cardData: cardResponse });
        }
      });

    if (!this._isImpactChartType(chartType)) {
      if (!this.pendingCardRequests[card.cardId]) {
        this.pendingCardRequests[card.cardId] = [];
      }
      this.pendingCardRequests[card.cardId].push(cardModel);
    }
  }

  _isImpactChartType(chartType) {
    return [
      Constants.DASHBOARD.chartTypes.scoreSummary,
      Constants.DASHBOARD.chartTypes.priorityMap,
      Constants.DASHBOARD.chartTypes.priorityIndex,
    ].includes(chartType);
  }

  _pollCardRestData(payload, options = {}) {
    const timeout = (options && options.timeout) || 5000;

    const cardModel = new DashboardDataModel(payload.card, {
      clientId: this.getClientId(),
      requestId: payload.card.requestId,
      useImpactDataService: true,
    });
    cardModel
      .fetch({
        error: (m, r) => {
          this._onCardRestDataError(payload, r.status);
        },
      })
      .then((response) => {
        const cardData = cardModel.toJSON();
        const action = this._getRestDataAction(payload.isOnDetail);
        if (response.ready) {
          this._cancelPollCardRestData(cardData.cardId);
          const completedCardData = {
            ...cardData,
            gdfEnabled: this.globalDateFilter[payload.dashboardId]?.isEnabled,
            status: ODI.COMPLETED,
          };
          this.dispatch(payload.callback, action, completedCardData);
          if (!payload.isOnDetail) {
            this._setRestData({ cardData: completedCardData });
          }
          return;
        }
        if (payload.callback) {
          payload.callback(cardData);
        } else {
          this.publish(action, cardData);
        }
        if (
          [
            ODI.TOO_FEW_RESPONDENTS,
            ODI.TOO_MANY_RESPONDENTS,
            ODI.RC_LIMIT_EXCEEDED,
            ODI.PARTITION_THRESHOLD_NOT_MET,
            ODI.NOT_ENOUGH_DATA_POINTS,
            ODI.ERROR,
            ODI.ENGINE_ERROR,
          ].includes(response.status)
        ) {
          const isDetailError =
            payload.isOnDetail &&
            cardData.status === ODI.ERROR &&
            cardData.errorType;
          if (!isDetailError) {
            this._setRestData({ cardData });
          }
          this._cancelPollCardRestData(cardData.cardId);
          return;
        }
        this.pollCardRestDataTimer = {
          ...this.pollCardRestDataTimer,
          [cardData.cardId]: {
            timeout: setTimeout(() => {
              this._pollCardRestData(payload);
            }, timeout),
            requestRef: cardModel._ajaxRef,
          },
        };
      });
  }

  _cancelOdi({ card = {}, doNotAbort, deleteReference }) {
    const { cardId, isOnDetail } = card;
    if (isOnDetail) {
      this.publish('dashboards-data:card:rest:data:update', {
        status: 'GENERATE',
      });
    } else {
      this.publish('dashboards-data:cards:rest:data', {
        cardId: card.cardId,
        status: 'GENERATE',
      });
    }
    this._cancelPollCardRestData(cardId, doNotAbort, deleteReference);
  }

  /**
   * Cancel ODI Polling
   *
   * @param {Int} id id of specific polling request - maps to cardId
   * @param {Boolean} doNotAbort defaults to false causing ajax request to be aborted
   * @param {Boolean} deleteReference defaults to true causing references to polling request to be deleted from memory
   */
  _cancelPollCardRestData(
    id = false,
    doNotAbort = false,
    deleteReference = true
  ) {
    const timeouts = this.pollCardRestDataTimer || {};
    if (!id) {
      _.each(timeouts, (obj) => {
        clearTimeout(obj.timeout);
        if (!doNotAbort) {
          obj.requestRef.abort();
        }
      });
      if (deleteReference) {
        this.pollCardRestDataTimer = {};
      }
    } else if (timeouts[id]) {
      clearTimeout(timeouts[id].timeout);
      if (!doNotAbort) {
        timeouts[id].requestRef.abort();
      }
      if (deleteReference) {
        delete this.pollCardRestDataTimer[id];
      }
    }
  }

  _getPollingReference(cardId) {
    return this.pollCardRestDataTimer && this.pollCardRestDataTimer[cardId];
  }

  /**
   * Resume Polling Requests
   * If we have already begun an impacts calculation for the card and have a reference
   to the polling data, resume polling again using the reference request data
   *
   * @param {Object} pollingReference object for the a specific polling request
   * @param {Boolean} isOnDetail  flag for if on card detail page
   */
  _resumePolling(pollingReference, isOnDetail) {
    const action = this._getRestDataAction(isOnDetail);
    const data = pollingReference?.requestRef?.responseJSON;
    this.publish(action, data);
    this._pollCardRestData({
      card: data,
      isOnDetail,
    });
  }

  _onCardRestDataError(payload, status, message, errorCode) {
    const { card = {} } = payload;
    const action = this._getRestDataAction(card.isOnDetail);
    const cardError = {
      cardId: card.cardId,
      hasErrors: true,
      unauthorized: status === 403,
      message: message,
      errorCode: errorCode,
      ready: true,
    };
    if (payload.callback) {
      payload.callback(cardError);
    } else {
      this.publish(action, cardError);
      if (!card.isOnDetail && status === 400 && message && errorCode) {
        this._setRestData({ cardData: cardError });
      }
    }
  }

  _setRestData(payload) {
    const { cardData, callback } = payload;
    this.publish('app:getData', {
      key: 'cardRestData',
      callback: (cardRestData) => {
        const { cardId } = cardData;
        cardRestData = cardRestData || {};
        cardRestData[cardId] = cardData;
        this.publish('app:updateData', { cardRestData, callback });
      },
    });
  }

  addTimezone(card, timeZone) {
    const dateRange = card.metadata.attributes.dateRange;
    if (dateRange && dateRange.t === 'DATE_TIME' && !dateRange.z) {
      card.metadata.attributes.dateRange.z = timeZone || this.timeZoneParam;
    }
    const isWordCloud =
      card.metadata.attributes.chartType ===
      Constants.DASHBOARD.chartTypes.wordCloud;
    if (dateRange && isWordCloud) {
      delete card.metadata.attributes.dateRange.z;
    }
  }

  shouldResetCache(currentCardData) {
    const { metadata = {}, isOnDetail } = currentCardData;
    const { attributes = {} } = metadata;
    const { viewType, cardSize, cardHeight, autoCycle } = attributes;
    const isMosaicAutoCycling =
      autoCycle &&
      viewType === 'card' &&
      cardSize === 'full' &&
      cardHeight === 'fullScreen' &&
      !isOnDetail;
    if (isMosaicAutoCycling) {
      return true;
    }
    if (!currentCardData.cacheExpirationTime) {
      return false;
    }
    const currentDate = new Date();
    const cardExpirationDate = moment(currentCardData.cacheExpirationTime);
    return cardExpirationDate.isBefore(currentDate);
  }

  _unsetRestData(payload) {
    const { cardId, callback } = payload;
    this.publish('app:getData', {
      key: 'cardRestData',
      callback: (cardRestData) => {
        cardRestData = cardRestData || {};
        cardRestData[cardId] = false;
        this.publish('app:updateData', { cardRestData, callback });
      },
    });
  }

  isCardToDate(card, currentDate) {
    const { dateRange, selectedDateId, dataSources } = card.metadata.attributes;
    const dataSourceType = dataSources && dataSources[0] && dataSources[0].type;
    //if it's a 'to date' date type and either it's event but not yesterday, it's not event or its a custom date range matching today
    return (
      (dateRange.n === 0 &&
        dateRange.r !== 'C' &&
        ((dataSourceType === 'event' && selectedDateId !== 'yesterday') ||
          dataSourceType !== 'event')) ||
      (dateRange.r === 'C' && currentDate.isSame(moment(dateRange.l), 'date'))
    );
  }

  cardHasHourlyData(card) {
    return (
      card.metadata.attributes.dimensions &&
      card.metadata.attributes.dimensions.length > 0 &&
      card.metadata.attributes.dimensions[0].key === 'HOURLY' &&
      card.series &&
      card.series.length > 0
    );
  }

  _resetWordcloudData(payload) {
    this.publish('app:getData', {
      key: 'cardRestData',
      callback: (cardRestData = {}) => {
        Object.keys(cardRestData)
          .filter((key) => cardRestData[key]?.metadata?.attributes)
          .forEach((key) => {
            const { attributes } = cardRestData[key].metadata;
            const { chartType, dataSources, metrics } = attributes;
            if (chartType === 'wordCloud') {
              if (
                payload &&
                this._hasCustomFeed(dataSources, payload.customFeedId)
              ) {
                delete cardRestData[key];
              } else if (!payload && this._hasMetric(metrics, 'KF')) {
                delete cardRestData[key];
              }
            }
          });

        this.publish('app:updateData', {
          cardRestData: cardRestData,
        });
      },
    });
  }

  _hasMetric(metrics = [], metricType) {
    return metrics.some((m) => m.type === metricType);
  }

  _hasCustomFeed(dataSources = [], customFeedId) {
    return dataSources.some(
      (x) => x.type === 'customFeed' && x.key === String(customFeedId)
    );
  }

  _clearAutoCyclePagination({ cardId }) {
    delete this.autoCyclePagination[cardId];
  }
}

export default DashboardsDataController;
